import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { MSALContext } from "../MSALContext";
import "./CompetencyMatrix.css";
import "bootstrap/dist/css/bootstrap.min.css";

const CompetencyMatrix = () => {
	const { msalInstance } = useContext(MSALContext);
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");

	useEffect(() => {
		fetchData();
	}, [msalInstance]);

	const fetchData = async () => {
		try {
			const response = await axios.get(
				`${process.env.REACT_APP_API_URL}/competency-matrix/data`
			);
			setData(response.data);
		} catch (error) {
			console.error("Error fetching data:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleSearchChange = (event) => {
		setSearchTerm(event.target.value);
	};

	const filteredData = data
		.filter((row) => row.Site.toLowerCase().includes(searchTerm.toLowerCase()))
		.filter(
			(row) =>
				!row.CompetencyLearningDomainBreakdown.endsWith("Enrollment Course")
		);

	// Calculate totals for each sidebar category
	const fundamentalCredits = filteredData
		.filter(
			(row) =>
				row.FundamentalOrElective === "Fundamental" &&
				row.LearningMethod !== "Under Development"
		)
		.reduce((sum, row) => sum + parseFloat(row.CreditHours || 0), 0);

	const electiveCredits = filteredData
		.filter(
			(row) =>
				row.FundamentalOrElective === "Elective" &&
				row.LearningMethod !== "Under Development"
		)
		.reduce((sum, row) => sum + parseFloat(row.CreditHours || 0), 0);

	const fundamentalCreditsUnderDev = filteredData
		.filter(
			(row) =>
				row.FundamentalOrElective === "Fundamental" &&
				row.LearningMethod === "Under Development"
		)
		.reduce((sum, row) => sum + parseFloat(row.CreditHours || 0), 0);

	const electiveCreditsUnderDev = filteredData
		.filter(
			(row) =>
				row.FundamentalOrElective === "Elective" &&
				row.LearningMethod === "Under Development"
		)
		.reduce((sum, row) => sum + parseFloat(row.CreditHours || 0), 0);

	const exportToCSV = () => {
		const csvContent = [
			[
				"Sites",
				"Core Competency",
				"Learning Method",
				"Fundamental or Elective",
				"Competency Learning Domain Breakdown",
				"Credit Hours",
				"Unique Code",
				"MIN Required PASS WOs",
				"Tac Name",
			],
			...filteredData.map((row) => [
				row.Site,
				row.CoreCompetency,
				row.LearningMethod,
				row.FundamentalOrElective,
				row.CompetencyLearningDomainBreakdown,
				row.CreditHours,
				row.UniqueCode,
				row.MINRequiredPASSWOs,
				row.TacName,
			]),
		]
			.map((e) => e.join(","))
			.join("\n");

		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute(
			"download",
			`CompetencyMatrix_${searchTerm || "AllSites"}.csv`
		);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	return (
		<div className="competency-matrix-container">
			<div className="sidebar">
				<div className="sidebar-card">
					<div className="card-title">Fundamental available credits</div>
					<div className="card-value">{fundamentalCredits}</div>
				</div>
				<div className="sidebar-card">
					<div className="card-title">Elective available credits</div>
					<div className="card-value">{electiveCredits}</div>
				</div>
				<div className="sidebar-card">
					<div className="card-title">
						Fundamental Credits Under Development
					</div>
					<div className="card-value">{fundamentalCreditsUnderDev}</div>
				</div>
				<div className="sidebar-card">
					<div className="card-title">Elective Credits Under Development</div>
					<div className="card-value">{electiveCreditsUnderDev}</div>
				</div>
			</div>

			<div className="competency-matrix-content container">
				<div className="d-flex justify-content-between align-items-center mb-3">
					<h1 className="my-0">Competency Matrix</h1>
					<div className="d-flex align-items-center">
						<input
							type="text"
							className="form-control"
							placeholder="Search by Site"
							value={searchTerm}
							onChange={handleSearchChange}
							style={{ maxWidth: "300px" }}
						/>
						<button
							onClick={exportToCSV}
							className="export-button"
							disabled={filteredData.length === 0}
						>
							Export CSV
						</button>
					</div>
				</div>
				{loading ? (
					<div className="competency-matrix-loading-container">
						<img
							src="/images/loadinggears.gif"
							alt="Loading..."
							className="competency-matrix-loading-image"
						/>
					</div>
				) : (
					<div className="table-responsive table-container">
						<table className="table table-bordered table-hover">
							<thead>
								<tr>
									<th className="sticky-header">Sites</th>
									<th className="sticky-header">Core Competency</th>
									<th className="sticky-header">Learning Method</th>
									<th className="sticky-header">Fundamental or Elective</th>
									<th className="sticky-header">
										Competency Learning Domain Breakdown
									</th>
									<th className="sticky-header">Credit Hours</th>
									<th className="sticky-header">Unique Code</th>
									<th className="sticky-header">MIN Required PASS WOs</th>
									<th className="sticky-header">Tac Name</th>
								</tr>
							</thead>

							<tbody>
								{Array.isArray(filteredData) &&
									filteredData.map((row, index) => (
										<tr
											key={index}
											className={
												row.LearningMethod === "Under Development"
													? "table-danger custom-danger"
													: ""
											}
										>
											<td>{row.Site}</td>
											<td>{row.CoreCompetency}</td>
											<td>{row.LearningMethod}</td>
											<td>{row.FundamentalOrElective}</td>
											<td>{row.CompetencyLearningDomainBreakdown}</td>
											<td>{row.CreditHours}</td>
											<td>{row.UniqueCode}</td>
											<td>{row.MINRequiredPASSWOs}</td>
											<td>{row.TacName}</td>
										</tr>
									))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
};

export default CompetencyMatrix;
